import i18n from 'i18next';
import detector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from '../locales/app/en.json';
import ar from '../locales/app/ar.json';
import ur from '../locales/app/ur.json';
import hi from '../locales/app/hi.json';
import bn from '../locales/app/bn.json';
import si from '../locales/app/si.json';
import ne from '../locales/app/ne.json';

export const APP_I18N_STORAGE_KEY = 'market_app_language';

export type AppLanguage = 'en' | 'ar' | 'ur' | 'hi' | 'bn' | 'si' | 'ne';

export interface AppLanguageOption {
  code: AppLanguage;
  nativeLabel: string;
  englishLabel: string;
  dir: 'ltr' | 'rtl';
}

export const APP_LANGUAGES: AppLanguageOption[] = [
  { code: 'en', nativeLabel: 'English', englishLabel: 'English', dir: 'ltr' },
  { code: 'ar', nativeLabel: 'العربية', englishLabel: 'Arabic', dir: 'rtl' },
  { code: 'ur', nativeLabel: 'اردو', englishLabel: 'Urdu', dir: 'rtl' },
  { code: 'hi', nativeLabel: 'हिन्दी', englishLabel: 'Hindi', dir: 'ltr' },
  { code: 'bn', nativeLabel: 'বাংলা', englishLabel: 'Bengali', dir: 'ltr' },
  { code: 'si', nativeLabel: 'සිංහල', englishLabel: 'Sinhala', dir: 'ltr' },
  { code: 'ne', nativeLabel: 'नेपाली', englishLabel: 'Nepali', dir: 'ltr' },
];

export const SUPPORTED_APP_LANGUAGES: AppLanguage[] = APP_LANGUAGES.map((l) => l.code);

const RTL_LANGUAGES = APP_LANGUAGES.filter((l) => l.dir === 'rtl').map((l) => l.code);

export const appI18n = i18n.createInstance();

if (!localStorage.getItem(APP_I18N_STORAGE_KEY)) {
  localStorage.setItem(APP_I18N_STORAGE_KEY, 'en');
}

appI18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
      ur: { translation: ur },
      hi: { translation: hi },
      bn: { translation: bn },
      si: { translation: si },
      ne: { translation: ne },
    },
    lng: localStorage.getItem(APP_I18N_STORAGE_KEY) || 'en',
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_APP_LANGUAGES,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: APP_I18N_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

export const isRtlLanguage = (language: string = appI18n.language): boolean =>
  RTL_LANGUAGES.some((code) => language.startsWith(code));

export const getLanguageDir = (language: string = appI18n.language): 'ltr' | 'rtl' =>
  isRtlLanguage(language) ? 'rtl' : 'ltr';

export const setAppLanguage = (language: AppLanguage): void => {
  localStorage.setItem(APP_I18N_STORAGE_KEY, language);
  void appI18n.changeLanguage(language);
};
